import { el } from './dom';

/**
 * Whether the pointer can hover.
 *
 * This is the capability the tooltip actually depends on, which is why it is the test
 * rather than a viewport width: a narrow desktop window can still hover, and a large
 * tablet cannot. Where it can't, the two buttons that end a line of questioning
 * explain themselves in a dialog instead - the explanation has to arrive *somehow*,
 * and on touch there is no hover state to put it in.
 */
export function pointerCanHover(): boolean {
  if (typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(hover: hover)').matches;
}

export interface ConfirmProps {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
}

/**
 * A modal confirm, built by hand rather than with `<dialog>`: `showModal` is still
 * missing from parts of the test and browser matrix, and the behaviour worth having
 * here - escape, backdrop, focus - is a dozen lines.
 */
export function confirmAction(props: ConfirmProps): void {
  const opener = document.activeElement;

  const cancel = el(
    'button',
    { class: 'btn btn--ghost', type: 'button', onclick: () => close() },
    'Cancel',
  );
  const confirm = el(
    'button',
    {
      class: 'btn btn--primary',
      type: 'button',
      onclick: () => {
        close();
        props.onConfirm();
      },
    },
    props.confirmLabel,
  );

  const dialog = el(
    'div',
    {
      class: 'dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'confirm-title',
      'aria-describedby': 'confirm-body',
    },
    el('h2', { class: 'dialog__title', id: 'confirm-title' }, props.title),
    el('p', { class: 'muted', id: 'confirm-body' }, props.body),
    el('div', { class: 'dialog__actions' }, cancel, confirm),
  );

  const overlay = el(
    'div',
    {
      class: 'overlay',
      // Only a tap on the backdrop itself dismisses; one inside the dialog bubbles here.
      onclick: (event: Event) => {
        if (event.target === overlay) close();
      },
    },
    dialog,
  );

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      close();
      return;
    }
    // Keep tab inside the dialog. Two buttons, so the trap is just the two ends.
    if (event.key !== 'Tab') return;
    const edge = event.shiftKey ? cancel : confirm;
    if (document.activeElement === edge) {
      event.preventDefault();
      (event.shiftKey ? confirm : cancel).focus();
    }
  }

  function close(): void {
    document.removeEventListener('keydown', onKeydown);
    overlay.remove();
    if (opener instanceof HTMLElement) opener.focus();
  }

  document.body.append(overlay);
  document.addEventListener('keydown', onKeydown);
  confirm.focus();
}
